import { useEffect, useState } from "react";
import {
  Alert,
  App,
  Button,
  Checkbox,
  Divider,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Spin,
  Tag,
  Upload,
} from "antd";
import { API_BASE_URL, apiFetch } from "../../services/apiClient";
import FirmaManuscrita from "../../Components/FirmaManuscrita";
import SelectorFirmasGuardadas from "./SelectorFirmasGuardadas";
import PdfFirmaEditor, { CampoFirma, PosicionFirma } from "./PdfFirmaEditor";
import { Contrato } from "../../services/contratos.api";

type Version = {
  id: string;
  hash: string;
  nombre: string;
  fecha: string;
  firmada: boolean;
  firmante?: string;
  rut?: string;
  fecha_firma?: string;
  campos?: CampoFirma[];
  campos_revision?: string | null;
};
type Entrada = {
  id: string;
  tipo: string;
  actor_nombre: string;
  fecha: string;
  texto?: string;
  privada?: boolean;
  version_id?: string;
};
type Historia = { activo: boolean; versiones: Version[]; eventos: Entrada[]; notas: Entrada[] };
type Respuesta = { success: boolean; data: Historia | null; correoEstado?: string };
const fecha = (s: string) =>
  new Date(s).toLocaleString("es-CL", {
    timeZone: "America/Santiago",
    dateStyle: "short",
    timeStyle: "short",
  });
const labels: Record<string, string> = {
  creado: "Solicitud creada",
  version_subida: "Nueva versión cargada",
  firmado: "Documento firmado",
  cancelado: "Solicitud cancelada",
  reactivado: "Solicitud reactivada",
  campos_actualizados: "Zonas de firma actualizadas",
};

export default function FirmaContratoModal({
  doc,
  isAdmin,
  onClose,
}: {
  doc: Contrato;
  isAdmin: boolean;
  onClose: () => void;
}) {
  const { modal, message } = App.useApp();
  const base = `/contratos/${doc.id}/firma`;
  const [historia, setHistoria] = useState<Historia | null>(null);
  const [cargando, setCargando] = useState(true);
  const [errorHistoria, setErrorHistoria] = useState(false);
  const [intentoHistoria, setIntentoHistoria] = useState(0);
  const [errorPdf, setErrorPdf] = useState(false);
  const [errorRevisionPdf, setErrorRevisionPdf] = useState(false);
  const [intentoPdf, setIntentoPdf] = useState(0);
  const [ocupado, setOcupado] = useState(false);
  const [versionId, setVersionId] = useState<string>();
  const [url, setUrl] = useState<string>();
  const [pdf, setPdf] = useState<Blob>();
  const [editorListo, setEditorListo] = useState(false);
  const [totalPaginas, setTotalPaginas] = useState(0);
  const [vista, setVista] = useState(false);
  const [verFirmado, setVerFirmado] = useState(false);
  const [verPreparado, setVerPreparado] = useState(false);
  const [nombre, setNombre] = useState("");
  const [rut, setRut] = useState("");
  const [firma, setFirma] = useState<string | null>(null);
  const [posicion, setPosicion] = useState<PosicionFirma | null>(null);
  const [camposBorrador, setCamposBorrador] = useState<CampoFirma[]>([]);
  const [tipoZona, setTipoZona] = useState<"firma" | "nombre" | "texto" | "imagen">("firma");
  const [campoSeleccionadoId, setCampoSeleccionadoId] = useState<string | null>(null);
  const [textoNuevo, setTextoNuevo] = useState("");
  const [tamanoFuenteNuevo, setTamanoFuenteNuevo] = useState(12);
  const [colorNuevo, setColorNuevo] = useState("#000000");
  const [imagenNueva, setImagenNueva] = useState<string | null>(null);
  const [revisionAceptada, setRevisionAceptada] = useState<{ revision: string; nombre: string; rut: string; firma: string | null } | null>(null);
  const [nota, setNota] = useState("");
  const [privada, setPrivada] = useState(false);
  const version = historia?.versiones.find((v) => v.id === versionId);
  const ultima = historia?.versiones.at(-1);
  // La aceptación pertenece al contenido concreto, no solo al ID de versión.
  // La comparación durante el render impide firmar incluso antes de que corran
  // los efectos que limpian el formulario al recibir una revisión distinta.
  const identidadRevision = version ? JSON.stringify([version.id, version.hash, version.campos_revision ?? null]) : null;
  const acepto = identidadRevision !== null && revisionAceptada?.revision === identidadRevision && revisionAceptada.nombre === nombre && revisionAceptada.rut === rut && revisionAceptada.firma === firma;
  const elementoSinAgregar = (!!posicion && !campoSeleccionadoId) || !!textoNuevo.trim() || !!imagenNueva;
  const cambiosSinGuardar = JSON.stringify(camposBorrador) !== JSON.stringify(version?.campos ?? []) || elementoSinAgregar;

  // La aceptación y el trazo pertenecen a la revisión revisada. El mismo PDF
  // puede cambiar de textos, imágenes o zonas sin cambiar su ID de versión.
  useEffect(() => {
    setFirma(null);
    setVerPreparado(false);
    setEditorListo(false);
    setPosicion(null);
    setCampoSeleccionadoId(null);
    setTextoNuevo("");
    setImagenNueva(null);
    setRevisionAceptada(null);
  }, [versionId, version?.hash, version?.campos_revision]);

  useEffect(() => {
    setCamposBorrador(version?.campos ?? []);
    setPosicion(null);
    setCampoSeleccionadoId(null);
    // La revisión cambia siempre que el servidor modifica las zonas; añadir
    // el array provocaría perder cambios sin guardar al actualizar una nota.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionId, version?.campos_revision]);

  useEffect(() => {
    let vigente = true;
    setCargando(true);
    setErrorHistoria(false);
    apiFetch<Respuesta>(base, { errorMsg: "No se pudo cargar el historial" }).then((r) => {
      if (!vigente) return;
      if (!r?.success || r.data === undefined) {
        setErrorHistoria(true);
        setCargando(false);
        return;
      }
      setHistoria(r?.data ?? null);
      setVersionId(r?.data?.versiones.at(-1)?.id);
      setCargando(false);
    });
    return () => {
      vigente = false;
    };
  }, [base, intentoHistoria]);

  useEffect(() => {
    setVista(false);
    setUrl(undefined);
    setPdf(undefined);
    setErrorPdf(false);
    setErrorRevisionPdf(false);
    if (!versionId) return;
    let cancelado = false;
    let creado: string | undefined;
    fetch(
      `${API_BASE_URL}${base}/versiones/${encodeURIComponent(versionId)}${verFirmado ? "?firmado=1" : (verPreparado || (!isAdmin && version?.campos_revision)) ? `?preparado=1&camposRevision=${encodeURIComponent(version?.campos_revision || "")}` : ""}`,
      { credentials: "include" },
    )
      .then((r) => {
        if (!r.ok) throw new Error(r.status === 409 ? "revision" : "pdf");
        return r.blob();
      })
      .then((blob) => {
        if (!cancelado) {
          creado = URL.createObjectURL(blob);
          setPdf(blob);
          setUrl(creado);
          setVista(true);
        }
      })
      .catch((error: unknown) => {
        if (!cancelado) { setErrorPdf(true); setErrorRevisionPdf(error instanceof Error && error.message === "revision"); }
      });
    return () => {
      cancelado = true;
      if (creado) URL.revokeObjectURL(creado);
    };
  }, [base, versionId, verFirmado, verPreparado, intentoPdf, isAdmin, version?.campos_revision]);

  const seleccionarCampo = (campo: CampoFirma) => {
    setCampoSeleccionadoId(campo.id);
    setPosicion(campo);
    setTipoZona(campo.tipo);
  };
  const cambiarPosicion = (p: PosicionFirma | null) => {
    setPosicion(p);
    if (!p) { setCampoSeleccionadoId(null); return; }
    if (campoSeleccionadoId) setCamposBorrador((previos) => previos.map((c) => c.id === campoSeleccionadoId ? { ...c, ...p } : c));
  };
  const cambiarCampo = (cambios: Partial<CampoFirma>) => {
    if (campoSeleccionadoId) setCamposBorrador((previos) => previos.map((c) => c.id === campoSeleccionadoId ? { ...c, ...cambios } : c));
  };
  const campoSeleccionado = camposBorrador.find((c) => c.id === campoSeleccionadoId);
  const actualizarFirma = (png: string | null) => {
    setRevisionAceptada(null);
    setFirma(png);
  };
  const confirmarDescartar = (accion: () => void) => {
    if (!isAdmin || !cambiosSinGuardar) { accion(); return; }
    modal.confirm({ title: "¿Descartar los cambios sin guardar?", content: "Los textos, imágenes o zonas que no guardaste se perderán.", okText: "Descartar cambios", cancelText: "Seguir editando", onOk: accion });
  };

  const mutar = async (path: string, body: unknown, success: string) => {
    setOcupado(true);
    const r = await apiFetch<Respuesta>(`${base}${path}`, {
      method: "POST",
      body: body instanceof FormData ? body : JSON.stringify(body),
      successMsg: success,
    });
    setOcupado(false);
    if (r?.success && r.data !== undefined) {
      setHistoria(r.data);
      if (path === "/firmar") {
        if (r.correoEstado === "enviado") message.success("Se solicitó el envío de una copia firmada a tu correo y a Aloja Fácil.");
        else message.warning("La firma quedó guardada, pero no se pudo enviar la copia por correo. Puedes descargarla o intentar enviarla nuevamente.");
      }
      return r.data;
    }
    return null;
  };
  const subir = async (file: File) => {
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      message.error("Selecciona un archivo PDF");
      return;
    }
    const datos = new FormData();
    datos.append("file", file);
    const r = await mutar("/versiones", datos, "Nueva versión disponible");
    if (r) {
      setVersionId(r.versiones.at(-1)?.id);
      setVerFirmado(false);
    }
  };
  const firmar = () => {
    if (!version || !vista || !editorListo || !nombre.trim() || !rut.trim() || !firma || !version.campos_revision || !version.campos?.some((c) => c.tipo === "firma") || !acepto) {
      message.warning("Lee el PDF, completa tus datos y revisa las zonas antes de aceptar");
      return;
    }
    modal.confirm({
      title: "¿Confirmas la firma?",
      content:
        "Al confirmar declaras que has leído el documento y estás de acuerdo con todo su contenido. La firma quedará registrada.",
      okText: "Confirmar firma",
      cancelText: "Volver",
      onOk: async () => {
        const r = await mutar(
          "/firmar",
          { nombre, rut, firma, camposRevision: version.campos_revision, acepto: true, version: version.id, hash: version.hash },
          "Contrato firmado",
        );
        if (r) {
          setFirma(null);
          setPosicion(null);
          setRevisionAceptada(null);
          setVerFirmado(true);
        }
      },
    });
  };

  return (
    <Modal
      title={`Firma de contrato · ${doc.titulo}`}
      open
      onCancel={() => confirmarDescartar(onClose)}
      footer={null}
      width={1050}
      destroyOnClose
    >
      {cargando ? (
        <Spin />
      ) : errorHistoria ? (
        <Alert
          type="error"
          showIcon
          message="No se pudo cargar la solicitud de firma"
          description="Comprueba tu conexión e inténtalo de nuevo. Si el problema continúa, contacta a la administración."
          action={<Button onClick={() => setIntentoHistoria((n) => n + 1)}>Reintentar</Button>}
        />
      ) : (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          {!historia && (
            <Alert
              type="info"
              showIcon
              message={
                isAdmin
                  ? "Sube el PDF que el cliente deberá leer y firmar."
                  : "Aún no hay un contrato disponible para firma."
              }
            />
          )}
          {isAdmin && (
            <Upload
              accept="application/pdf,.pdf"
              showUploadList={false}
              beforeUpload={(file) => {
                void subir(file);
                return false;
              }}
            >
              <Button loading={ocupado}>
                {historia ? "Subir nueva versión PDF" : "Subir PDF para firma"}
              </Button>
            </Upload>
          )}
          {historia && (
            <>
              <Space wrap>
                <Tag color={historia.activo ? "green" : "red"}>
                  {historia.activo ? "Activo" : "Cancelado"}
                </Tag>
                {isAdmin && (
                  <Button
                    loading={ocupado}
                    onClick={() =>
                      modal.confirm({
                        title: historia.activo
                          ? "¿Cancelar esta solicitud?"
                          : "¿Reactivar esta solicitud?",
                        content: "El cambio quedará registrado en el historial.",
                        onOk: () =>
                          mutar("/estado", { activo: !historia.activo }, "Estado actualizado").then(
                            () => undefined,
                          ),
                      })
                    }
                  >
                    {historia.activo ? "Cancelar solicitud" : "Reactivar solicitud"}
                  </Button>
                )}
              </Space>
              <Select
                aria-label="Versión del contrato"
                value={versionId}
                onChange={(id) => confirmarDescartar(() => {
                  setVersionId(id);
                  setVerFirmado(false);
                  setVerPreparado(false);
                })}
                style={{ width: "100%" }}
                options={historia.versiones.map((v, i) => ({
                  value: v.id,
                  label: `Versión ${i + 1} · ${fecha(v.fecha)}${v.firmada ? " · Firmada" : " · Sin firmar"}`,
                }))}
              />
              {version && (
                <>
                  <Space wrap>
                    <Button
                      onClick={() => { setVerFirmado(false); setVerPreparado(false); }}
                      type={!verFirmado && !verPreparado ? "primary" : "default"}
                    >
                      {!isAdmin && version.campos_revision ? "PDF preparado" : "PDF original"}
                    </Button>
                    {isAdmin && !version.firmada && version.campos_revision && <Button
                      disabled={cambiosSinGuardar} onClick={() => { setVerFirmado(false); setVerPreparado(true); }} type={verPreparado ? "primary" : "default"}
                    >Ver PDF preparado guardado</Button>}
                    {version.firmada && (
                      <Button
                        onClick={() => { setVerFirmado(true); setVerPreparado(false); }}
                        type={verFirmado ? "primary" : "default"}
                      >
                        PDF firmado
                      </Button>
                    )}
                    {url && (
                      <Button
                        href={url}
                        download={verFirmado ? "contrato-firmado.pdf" : verPreparado || (!isAdmin && version.campos_revision) ? "contrato-preparado.pdf" : "contrato.pdf"}
                      >
                        Descargar PDF
                      </Button>
                    )}
                    {url && (
                      <Button href={url} target="_blank" rel="noopener noreferrer">
                        Abrir PDF en otra ventana
                      </Button>
                    )}
                    {!isAdmin && (
                      <Button
                        loading={ocupado}
                        onClick={() =>
                          void mutar(
                            `/versiones/${version.id}/correo`,
                            {},
                            "Documento enviado a tu correo",
                          )
                        }
                      >
                        Enviar a mi correo{version.firmada ? " (firmado)" : ""}
                      </Button>
                    )}
                  </Space>
                  {isAdmin && !version.firmada && cambiosSinGuardar && <Alert type="warning" showIcon message="Hay cambios sin guardar" description="Guarda los elementos para poder revisar o descargar el PDF preparado actualizado." />}
                  {isAdmin && verPreparado && <Alert type="info" showIcon message="Estás viendo el PDF preparado guardado" description="Para volver a editar, selecciona PDF original." />}
                  {url ? (
                    <iframe
                      title="Visor del contrato PDF"
                      src={url}
                      style={{ width: "100%", height: 520, border: "1px solid #ddd" }}
                    />
                  ) : errorPdf && errorRevisionPdf ? (
                    <Alert type="warning" showIcon message="El documento preparado cambió" description="Recarga el historial y revisa la nueva versión antes de continuar." action={<Button onClick={() => setIntentoHistoria((n) => n + 1)}>Recargar documento</Button>} />
                  ) : errorPdf ? (
                    <Alert
                      type="error"
                      showIcon
                      message="No se pudo abrir el PDF o verificar su integridad"
                      description="Inténtalo de nuevo. Si el problema continúa, contacta a la administración antes de firmar."
                      action={<Button onClick={() => setIntentoPdf((n) => n + 1)}>Reintentar PDF</Button>}
                    />
                  ) : (
                    <Spin aria-label="Cargando PDF" />
                  )}
                  <small>
                    Usa los controles del visor para ampliar y desplazarte. Si tu dispositivo no
                    muestra el PDF, descárgalo.
                  </small>
                  {version.firmada && (
                    <Alert
                      type="success"
                      showIcon
                      message={`Firmado por ${version.firmante} · RUT ${version.rut} · ${fecha(version.fecha_firma!)}`}
                    />
                  )}
                  {isAdmin && historia.activo && version.id === ultima?.id && !version.firmada && pdf && !verPreparado && (
                    <>
                      <Divider>Preparar documento</Divider>
                      <p>Agrega texto y zonas para el cliente. Selecciona una zona existente para moverla o cambiar su tamaño; guarda los cambios antes de pedir la firma.</p>
                      <Alert type="info" showIcon message="El texto agregado se incorpora al PDF preparado y al firmado. El contenido original del PDF se conserva." />
                      <Space wrap>
                        <Select aria-label="Tipo de zona" value={tipoZona} onChange={(v) => { setTipoZona(v); setPosicion(null); setCampoSeleccionadoId(null); }} options={[{ value: "firma", label: "Firma del cliente" }, { value: "nombre", label: "Nombre del cliente" }, { value: "texto", label: "Texto" }, { value: "imagen", label: "Imagen PNG de administración" }]} style={{ width: 270 }} />
                        {campoSeleccionadoId && <Button onClick={() => { setCampoSeleccionadoId(null); setPosicion(null); }}>Agregar otro elemento</Button>}
                        {elementoSinAgregar && <Button onClick={() => { setPosicion(null); setCampoSeleccionadoId(null); setTextoNuevo(""); setImagenNueva(null); }}>Descartar elemento sin agregar</Button>}
                      </Space>
                      <PdfFirmaEditor pdf={pdf} firma={null} posicion={posicion} onChange={cambiarPosicion} campos={camposBorrador} tipoZona={tipoZona} campoSeleccionadoId={campoSeleccionadoId} onSeleccionarCampo={seleccionarCampo} onPageCount={setTotalPaginas} permitirUbicar />
                      {posicion && <Space wrap>
                        {campoSeleccionadoId && <label>Página <InputNumber aria-label="Página del elemento" min={1} max={totalPaginas || 1} value={posicion.pagina} onChange={(v) => { if (typeof v === "number" && Number.isInteger(v) && v >= 1 && v <= totalPaginas) cambiarPosicion({ ...posicion, pagina: v }); }} /></label>}
                        <label>Ancho (%) <InputNumber aria-label="Ancho de la zona en porcentaje" min={5} max={100} value={Math.round(posicion.ancho * 100)} onChange={(v) => { if (typeof v === "number" && v >= 5 && v <= 100) { const ancho = v / 100; cambiarPosicion({ ...posicion, ancho, x: Math.min(posicion.x, 1 - ancho) }); } }} /></label>
                        <label>Alto (%) <InputNumber aria-label="Alto de la zona en porcentaje" min={1} max={100} value={Math.round(posicion.alto * 100)} onChange={(v) => { if (typeof v === "number" && v >= 1 && v <= 100) { const alto = v / 100; cambiarPosicion({ ...posicion, alto, y: Math.min(posicion.y, 1 - alto) }); } }} /></label>
                      </Space>}
                      {tipoZona === "texto" && <>
                        <Input.TextArea aria-label="Texto para agregar al contrato" placeholder="Escribe el texto que irá en el PDF" maxLength={2000} value={campoSeleccionado?.texto ?? textoNuevo} onChange={(e) => campoSeleccionado ? cambiarCampo({ texto: e.target.value }) : setTextoNuevo(e.target.value)} />
                        <Space wrap>
                          <label>Tamaño <InputNumber aria-label="Tamaño del texto" min={6} max={72} value={campoSeleccionado?.tamanoFuente ?? tamanoFuenteNuevo} onChange={(n) => { if (typeof n === "number") campoSeleccionado ? cambiarCampo({ tamanoFuente: n }) : setTamanoFuenteNuevo(n); }} /></label>
                          <label>Color <input aria-label="Color del texto" type="color" value={campoSeleccionado?.color ?? colorNuevo} onChange={(e) => campoSeleccionado ? cambiarCampo({ color: e.target.value }) : setColorNuevo(e.target.value)} /></label>
                        </Space>
                      </>}
                      {tipoZona === "imagen" && <>
                        <Alert type="info" showIcon message="Imagen añadida por administración" description="Puede ser una firma o imagen PNG de tu cuenta. Es parte del contenido preparado, no representa la firma ni la aceptación del cliente." />
                        <SelectorFirmasGuardadas firmaActual={null} onSelect={(png) => {
                          const otras = camposBorrador.filter((c) => c.tipo === "imagen" && c.id !== campoSeleccionadoId);
                          if (!png.startsWith("data:image/png;base64,") || png.length > 300000 || otras.reduce((s, c) => s + (c.png?.length || 0), 0) + png.length > 600000) { message.error("La imagen supera el límite permitido para el documento."); return; }
                          if (campoSeleccionado) cambiarCampo({ png }); else setImagenNueva(png);
                        }} />
                        {(campoSeleccionado?.png || imagenNueva) && <img src={campoSeleccionado?.png || imagenNueva || undefined} alt="Imagen de administración elegida" style={{ maxWidth: 240, maxHeight: 100, objectFit: "contain" }} />}
                      </>}
                      <Space wrap>
                        <Button disabled={!!campoSeleccionadoId || !posicion || camposBorrador.length >= 20 || (tipoZona === "texto" && !textoNuevo.trim()) || (tipoZona === "imagen" && (!imagenNueva || camposBorrador.filter((c) => c.tipo === "imagen").length >= 3))} onClick={() => { if (posicion) { setCamposBorrador([...camposBorrador, { ...posicion, tipo: tipoZona, id: `nueva-${Date.now()}-${camposBorrador.length}`, ...(tipoZona === "texto" ? { texto: textoNuevo, tamanoFuente: tamanoFuenteNuevo, color: colorNuevo } : {}), ...(tipoZona === "imagen" ? { png: imagenNueva! } : {}) }]); setPosicion(null); setTextoNuevo(""); setImagenNueva(null); } }}>Agregar {tipoZona === "texto" ? "texto" : tipoZona === "imagen" ? "imagen" : "zona"}</Button>
                        <Button type="primary" loading={ocupado} disabled={elementoSinAgregar || !camposBorrador.some((c) => c.tipo === "firma")} onClick={() => void mutar(`/versiones/${version.id}/campos`, { hash: version.hash, camposRevision: version.campos_revision ?? null, campos: camposBorrador }, "Zonas guardadas")}>Guardar zonas</Button>
                      </Space>
                      {camposBorrador.map((c, i) => <div key={c.id}>{c.tipo === "firma" ? "Firma del cliente" : c.tipo === "texto" ? `Texto: ${c.texto}` : c.tipo === "imagen" ? "Imagen de administración" : "Nombre"} · página {c.pagina} <Button size="small" onClick={() => seleccionarCampo(c)}>Editar</Button> <Button size="small" onClick={() => { setCamposBorrador(camposBorrador.filter((_, j) => i !== j)); if (campoSeleccionadoId === c.id) { setCampoSeleccionadoId(null); setPosicion(null); } }}>Quitar</Button></div>)}
                    </>
                  )}
                  {!isAdmin && historia.activo && version.id === ultima?.id && !version.firmada && !version.campos_revision && <Alert type="info" showIcon message="La administración aún está preparando las zonas de firma de este contrato." />}
                  {!isAdmin && historia.activo && version.id === ultima?.id && !version.firmada && version.campos_revision && (
                    <>
                      <Divider>Firmar este contrato</Divider>
                      <Input
                        aria-label="Nombre completo"
                        placeholder="Nombre completo"
                        value={nombre}
                        maxLength={120}
                        onChange={(e) => { setRevisionAceptada(null); setNombre(e.target.value); }}
                      />
                      <Input
                        aria-label="RUT"
                        placeholder="RUT, por ejemplo 12.345.678-5"
                        value={rut}
                        maxLength={14}
                        onChange={(e) => { setRevisionAceptada(null); setRut(e.target.value); }}
                      />
                      <FirmaManuscrita key={identidadRevision ?? version.id} onChange={actualizarFirma} />
                      <SelectorFirmasGuardadas firmaActual={firma} onSelect={actualizarFirma} />
                      {firma && <div><strong>Firma que se usará en las zonas del cliente:</strong><br /><img src={firma} alt="Firma seleccionada para este contrato" style={{ maxWidth: 260, maxHeight: 100, objectFit: "contain" }} /></div>}
                      {pdf && <PdfFirmaEditor pdf={pdf} firma={firma} nombre={nombre} posicion={null} onChange={() => {}} onReady={setEditorListo} campos={(version.campos || []).filter((c) => c.tipo === "firma" || c.tipo === "nombre")} permitirUbicar={false} />}
                      <Checkbox checked={acepto} onChange={(e) => setRevisionAceptada(e.target.checked && identidadRevision ? { revision: identidadRevision, nombre, rut, firma } : null)}>
                        He leído el documento y estoy de acuerdo con todo su contenido. Acepto
                        firmarlo electrónicamente.
                      </Checkbox>
                      <Button
                        type="primary"
                        loading={ocupado}
                        disabled={!vista || !editorListo || !acepto || !firma || !version.campos?.some((c) => c.tipo === "firma") || !nombre.trim() || !rut.trim()}
                        onClick={firmar}
                      >
                        Revisar y firmar
                      </Button>
                    </>
                  )}
                </>
              )}
              <Divider>Notas</Divider>
              {historia.notas.length === 0 ? (
                <span>No hay notas.</span>
              ) : (
                historia.notas.map((n) => (
                  <div key={n.id}>
                    <strong>{n.actor_nombre}</strong> · {fecha(n.fecha)}{" "}
                    {n.privada && <Tag>Privada para administración</Tag>}
                    <p style={{ whiteSpace: "pre-wrap" }}>{n.texto}</p>
                  </div>
                ))
              )}
              <Input.TextArea
                aria-label="Nueva nota"
                value={nota}
                maxLength={3000}
                onChange={(e) => setNota(e.target.value)}
                placeholder="Escribe una nota"
              />
              {isAdmin && (
                <Checkbox checked={privada} onChange={(e) => setPrivada(e.target.checked)}>
                  Nota privada para administración
                </Checkbox>
              )}
              <Button
                loading={ocupado}
                disabled={!nota.trim()}
                onClick={async () => {
                  if (await mutar("/notas", { texto: nota, privada }, "Nota agregada")) setNota("");
                }}
              >
                Agregar nota
              </Button>
              <Divider>Historial</Divider>
              {historia.eventos
                .slice()
                .reverse()
                .map((e) => (
                  <div key={e.id}>
                    {fecha(e.fecha)} · <strong>{labels[e.tipo] || e.tipo}</strong> ·{" "}
                    {e.actor_nombre}
                  </div>
                ))}
            </>
          )}
        </Space>
      )}
    </Modal>
  );
}
